import { useGetIdentity, useLogout } from '@refinedev/core';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { Sun, Moon, LogOut, ChevronDown } from 'lucide-react';
import { useState, useEffect, type FC } from 'react';
import { HelpButton } from '@/components/onboarding/HelpButton';

interface Identity {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  roles: string[];
}

interface HeaderProps {
  onRestartWelcomeTour?: () => void;
}

export const Header: FC<HeaderProps> = ({ onRestartWelcomeTour }) => {
  const { data: identity } = useGetIdentity<Identity>();
  const { mutate: logout } = useLogout();
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('fc-admin-theme') === 'dark';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('fc-admin-theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('fc-admin-theme', 'light');
    }
  }, [isDark]);

  const initials = identity?.name?.charAt(0)?.toUpperCase() ?? 'A';

  const menuItems: MenuProps['items'] = [
    {
      key: 'info',
      label: (
        <div className="px-1 py-1">
          <p className="text-sm font-medium text-slate-900 dark:text-white">{identity?.name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{identity?.email}</p>
          <p className="mt-1 text-xs font-medium text-blue-600 dark:text-blue-400">
            {identity?.roles?.join(', ')}
          </p>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogOut className="w-3.5 h-3.5" />,
      label: 'Se deconnecter',
      danger: true,
      onClick: () => logout(),
    },
  ];

  return (
    <header className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3 transition-colors duration-200">
      {/* Logo + Titre */}
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-xs font-bold text-white shadow-sm">
          FC
        </div>
        <span className="text-sm font-semibold text-slate-900 dark:text-white">
          Feedback Orchestrator
        </span>
        <span className="hidden sm:inline rounded-md bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
          Admin
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {/* Help button */}
        <HelpButton
          onRestartPageTour={() => {
            // Dispatched via custom event — les pages ecoutent
            window.dispatchEvent(new CustomEvent('restart-page-tour'));
          }}
          onRestartWelcomeTour={() => onRestartWelcomeTour?.()}
        />

        {/* Dark mode toggle */}
        <button
          onClick={() => setIsDark(!isDark)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-all duration-200"
          title={isDark ? 'Mode clair' : 'Mode sombre'}
        >
          {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
        </button>

        {/* User dropdown */}
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <button className="flex items-center gap-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-600 transition-all duration-200">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-xs font-bold text-white shadow-sm">
              {initials}
            </div>
            <span className="hidden text-sm font-medium text-slate-700 dark:text-slate-200 sm:inline">
              {identity?.name ?? 'Admin'}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
          </button>
        </Dropdown>
      </div>
    </header>
  );
};
